import { useState } from 'react';
import { useSnackbar } from 'notistack';
import {
  useAddLines,
  useItemBulkLookup,
  useItemCandidates,
} from '@/api/sourcing/sourcing.hooks';
import type { BulkItemLookupResponse } from '@/api/sourcing/sourcing.types';
import { DsButton, DsModal, DsSearchInput, DsTextarea } from '../../ds';

interface Props {
  tenderId: string;
  existingItemIds: string[];
  onClose: () => void;
}

type Mode = 'search' | 'bulk';

export function LinePicker({ tenderId, existingItemIds, onClose }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [mode, setMode] = useState<Mode>('search');
  const [q, setQ] = useState('');
  const { data: candidates, isFetching } = useItemCandidates(q);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const addLines = useAddLines();
  const existing = new Set(existingItemIds);

  // Bulk paste-search state. Each pasted token is resolved to a catalog item
  // (by article number or EAN); resolved tokens auto-select, unresolved ones
  // are surfaced separately so the buyer can fix typos.
  const [bulkText, setBulkText] = useState('');
  const [bulkResult, setBulkResult] = useState<BulkItemLookupResponse | null>(
    null,
  );
  const bulkLookup = useItemBulkLookup();

  const toggle = (itemId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };

  const runBulkLookup = () => {
    const tokens = bulkText
      .split(/[\s,;]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (tokens.length === 0) return;
    bulkLookup.mutate(tokens, {
      onSuccess: (res) => {
        setBulkResult(res);
        // Auto-select every matched item that isn't already on the tender.
        setSelected((prev) => {
          const next = new Set(prev);
          for (const m of res.matches) {
            if (!existing.has(m.item.id)) next.add(m.item.id);
          }
          return next;
        });
        const matched = res.matches.length;
        const missing = res.unresolved.length;
        enqueueSnackbar(
          missing > 0
            ? `${matched} gefunden, ${missing} nicht zugeordnet.`
            : `${matched} Artikel gefunden.`,
          { variant: missing > 0 ? 'warning' : 'success' },
        );
      },
    });
  };

  const handleAdd = () => {
    if (selected.size === 0) return;
    addLines.mutate(
      {
        id: tenderId,
        body: { items: [...selected].map((itemId) => ({ itemId })) },
      },
      {
        onSuccess: (lines) => {
          enqueueSnackbar(`${lines.length} Artikel hinzugefügt.`, {
            variant: 'success',
          });
          onClose();
        },
      },
    );
  };

  // Items already resolved in bulk mode but not in the candidate list need to
  // be rendered explicitly so the buyer can see what's been picked up.
  const bulkMatchedRows = bulkResult?.matches ?? [];

  return (
    <DsModal
      open
      onClose={onClose}
      title="Artikel hinzufügen"
      description="Artikel aus dem Katalog suchen und der Ausschreibung hinzufügen."
      footer={
        <>
          <DsButton onClick={onClose}>Abbrechen</DsButton>
          <DsButton
            variant="primary"
            disabled={selected.size === 0 || addLines.isPending}
            onClick={handleAdd}
          >
            {selected.size > 0
              ? `${selected.size} hinzufügen`
              : 'Hinzufügen'}
          </DsButton>
        </>
      }
    >
      <div className="sc-picker">
        <div className="sc-picker-modebar" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'search'}
            className={`sc-picker-mode${mode === 'search' ? ' is-active' : ''}`}
            onClick={() => setMode('search')}
          >
            Suche
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'bulk'}
            className={`sc-picker-mode${mode === 'bulk' ? ' is-active' : ''}`}
            onClick={() => setMode('bulk')}
          >
            Mehrfachsuche
          </button>
        </div>
        {mode === 'search' ? (
          <>
            <DsSearchInput
              value={q}
              onSearch={setQ}
              debounce={250}
              placeholder="Artikelnr., Bezeichnung oder EAN (min. 2 Zeichen)…"
              autoFocus
            />
            <div className="sc-picker-list ds-scroll">
              {q.trim().length < 2 ? (
                <p className="sc-hint">Suchbegriff eingeben…</p>
              ) : isFetching ? (
                <p className="sc-hint">Sucht…</p>
              ) : (candidates ?? []).length === 0 ? (
                <p className="sc-hint">Keine Artikel gefunden.</p>
              ) : (
                (candidates ?? []).map((item) => {
                  const added = existing.has(item.id);
                  return (
                    <label
                      key={item.id}
                      className={`sc-picker-row${added ? ' is-disabled' : ''}`}
                    >
                      <input
                        type="checkbox"
                        disabled={added}
                        checked={added || selected.has(item.id)}
                        onChange={() => toggle(item.id)}
                      />
                      <span className="sc-picker-main">
                        <span className="sc-picker-title">{item.name}</span>
                        <span className="sc-picker-sub">
                          {item.id}
                          {item.ean ? ` · ${item.ean}` : ''}
                        </span>
                      </span>
                      {added && (
                        <span className="ds-pill muted">bereits enthalten</span>
                      )}
                    </label>
                  );
                })
              )}
            </div>
          </>
        ) : (
          <>
            <DsTextarea
              rows={5}
              value={bulkText}
              autoFocus
              placeholder="Artikelnummern oder EANs — eine pro Zeile, oder kommagetrennt…"
              onChange={(e) => setBulkText(e.target.value)}
            />
            <div className="sc-picker-bulk-actions">
              <DsButton
                size="small"
                onClick={runBulkLookup}
                disabled={bulkText.trim().length === 0 || bulkLookup.isPending}
              >
                {bulkLookup.isPending ? 'Suche…' : 'Zuordnen'}
              </DsButton>
              {bulkResult && (
                <span className="sc-hint">
                  {bulkResult.matches.length} gefunden ·{' '}
                  {bulkResult.unresolved.length} nicht zugeordnet
                </span>
              )}
            </div>
            <div className="sc-picker-list ds-scroll">
              {bulkMatchedRows.length === 0 && !bulkResult && (
                <p className="sc-hint">
                  Liste einfügen und „Zuordnen“ drücken.
                </p>
              )}
              {bulkMatchedRows.map(({ token, item }) => {
                const added = existing.has(item.id);
                return (
                  <label
                    key={item.id}
                    className={`sc-picker-row${added ? ' is-disabled' : ''}`}
                  >
                    <input
                      type="checkbox"
                      disabled={added}
                      checked={added || selected.has(item.id)}
                      onChange={() => toggle(item.id)}
                    />
                    <span className="sc-picker-main">
                      <span className="sc-picker-title">{item.name}</span>
                      <span className="sc-picker-sub">
                        {item.id}
                        {item.ean ? ` · ${item.ean}` : ''}
                        {token !== item.id ? ` · gesucht: ${token}` : ''}
                      </span>
                    </span>
                    {added && (
                      <span className="ds-pill muted">bereits enthalten</span>
                    )}
                  </label>
                );
              })}
              {(bulkResult?.unresolved ?? []).length > 0 && (
                <div className="sc-picker-unresolved">
                  <span className="sc-picker-sub">Nicht zugeordnet:</span>{' '}
                  {bulkResult!.unresolved.join(', ')}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </DsModal>
  );
}
